import React, { useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  DatePicker,
  TimePicker,
} from 'antd';
import { DateTime } from 'luxon';
import moment from 'moment';
import { PropTypes } from 'prop-types';

import { isNullOrUndefined } from '../../helpers/helpers';
import {
  getRangeFormat,
  getAllDayValues,
  formatTime,
} from '../../helpers/time';

import { localToZoneTS, zoneToLocalTS } from '../../clock/ManualEntry/manualEntryHelpers';
import OnTraccrCheckbox from './OnTraccrCheckbox';
import Colors from '../../constants/Colors';

export default function OnTraccrDatePickerRedesign({
  value: [startTime, endTime] = [],
  onChange,
  gutter = 20,
  hideCheckbox,
  hideTime,
  singleDay,
  allowClear,
  style = {},
  timezone = DateTime.local().zoneName,
  isFieldDisplay,
  isHalfWidth,
}) {
  const startDT = useMemo(() => (
    isNullOrUndefined(startTime)
      ? null
      : DateTime.fromMillis(startTime, { zone: timezone })
  ), [startTime, timezone]);
  const endDT = useMemo(() => (
    isNullOrUndefined(endTime)
      ? null
      : DateTime.fromMillis(endTime, { zone: timezone })
  ), [endTime, timezone]);
  const startMoment = useMemo(() => (
    isNullOrUndefined(startTime)
      ? null
      : moment(zoneToLocalTS(startTime, timezone))
  ), [startTime]);
  const endMoment = useMemo(() => (
    isNullOrUndefined(endTime)
      ? null
      : moment(zoneToLocalTS(endTime, timezone))
  ), [endTime]);
  const {
    isAllDay,
  } = useMemo(() => getAllDayValues(startDT, endDT), [startDT, endDT]);

  const rangePickerProps = useMemo(() => (
    (isAllDay || hideTime)
      ? false
      : {
        format: 'hh:mm a',
        minuteStep: 15,
        use12Hours: true,
      }
  ), [isAllDay, hideTime]);

  const onDateChanged = useCallback((dateMoment) => {
    if (!moment.isMoment(dateMoment)) {
      onChange([]);
      return;
    }
    const newZoneTs = localToZoneTS(dateMoment.valueOf(), timezone);
    const newDT = DateTime.fromMillis(newZoneTs, { zone: timezone });
    const safeStartDT = startDT || newDT;

    const updateObj = { day: newDT.day, month: newDT.month, year: newDT.year };
    const newStartDT = safeStartDT.set(updateObj);
    const newEndDT = endDT ? endDT.set(updateObj) : undefined;
    let newStart = newStartDT.toMillis();
    let newEnd = newEndDT ? newEndDT.toMillis() : undefined;
    if (hideTime) {
      newStart = newStartDT.startOf('day').toMillis();
      newEnd = newStartDT.endOf('day').toMillis();
    }
    onChange([newStart, newEnd]);
  }, [startDT, endDT, onChange, timezone, hideTime]);

  const onRangeChange = useCallback((newRepeat) => {
    const [m1, m2] = newRepeat || [];
    if (!moment.isMoment(m1) || !moment.isMoment(m2)) {
      onChange([]);
      return;
    }
    const startTS = localToZoneTS(m1.valueOf(), timezone);
    const endTS = localToZoneTS(m2.valueOf(), timezone);
    if (!isAllDay && !hideTime) {
      onChange([startTS, endTS]);
      return;
    }
    const newStart = DateTime.fromMillis(startTS, { zone: timezone }).startOf('day');
    const newEnd = DateTime.fromMillis(endTS, { zone: timezone }).endOf('day');
    onChange([newStart.toMillis(), newEnd.toMillis()]);
  }, [isAllDay, hideTime, onChange, timezone]);

  const onTimeChange = useCallback((newTimes) => {
    const [m1, m2] = newTimes || [];
    if (!moment.isMoment(m1) || !moment.isMoment(m2)) {
      onChange([]);
      return;
    }
    const startTs = localToZoneTS(m1.valueOf(), timezone);
    const endTs = localToZoneTS(m2.valueOf(), timezone);
    const newStart = DateTime.fromMillis(startTs, { zone: timezone });
    const newEnd = DateTime.fromMillis(endTs, { zone: timezone });
    if (startDT) {
      onChange([
        startDT.set({ hour: newStart.hour, minute: newStart.minute }).toMillis(),
        startDT.set({ hour: newEnd.hour, minute: newEnd.minute }).toMillis(),
      ]);
      return;
    }
    onChange([newStart.toMillis(), newEnd.toMillis()]);
  }, [startDT, endDT, onChange, timezone]);

  const toggleAllDay = useCallback(() => {
    const nowTs = DateTime.local().toMillis();
    const zoneNow = DateTime.fromMillis(localToZoneTS(nowTs, timezone), { zone: timezone });
    const safeStart = startDT || zoneNow;
    const safeEnd = endDT || zoneNow;
    if (isAllDay) {
      const newStart = safeStart
        .set({ hour: zoneNow.hour, minutes: zoneNow.minute, seconds: 0 })
        .toMillis();
      const newEnd = safeEnd
        .set({ hour: zoneNow.hour, minutes: zoneNow.minute, seconds: 0 })
        .toMillis();
      onChange([newStart, newEnd]);
    } else {
      const newStart = safeStart.startOf('day').toMillis();
      const newEnd = safeEnd.endOf('day').toMillis();
      onChange([newStart, newEnd]);
    }
  }, [startDT, endDT, isAllDay, onChange, timezone]);

  const rangeMode = !singleDay;

  const showTime = !isAllDay && !hideTime;

  const displayValue = useMemo(() => {
    if (!startTime || !endTime) {
      return null;
    }

    const format = showTime ? DateTime.DATETIME_MED : DateTime.DATE_MED;
    const formattedStart = formatTime(startTime, format);
    const formattedEnd = formatTime(endTime, format);

    return (
      <Row style={{ ...style, gap: 10 }}>
        <div>
          <span style={{ color: Colors.GRAY_800 }}>Start Time: </span>
          <span>{formattedStart}</span>
        </div>
        <div style={{ color: Colors.GRAY_800 }}>
          ⟶
        </div>
        <div>
          <span style={{ color: Colors.GRAY_800 }}>End Time: </span>
          <span>{formattedEnd}</span>
        </div>
      </Row>
    );
  }, [startTime, endTime, showTime, isFieldDisplay, style]);

  if (isFieldDisplay) {
    return (
      <div className="form-field-display-value-text">
        {displayValue}
      </div>
    );
  }

  const showCheckBoxOnNewLine = rangeMode && isHalfWidth;

  const datePickerWideSpan = showCheckBoxOnNewLine ? 24 : 12;

  const rangePickerSpan = isHalfWidth ? 24 : 10;
  const datePickerSpan = isHalfWidth || rangeMode ? datePickerWideSpan : 6;
  const showRangePicker = !hideTime && !(singleDay && isAllDay);

  const pickerComponent = (
    <Col span={rangeMode ? 0 : rangePickerSpan}>
      <TimePicker.RangePicker
        style={{ width: isHalfWidth ? 285 : '100%' }}
        className="form-field-datetime-container"
        format="hh:mm a"
        minuteStep={15}
        use12Hours
        allowClear={allowClear}
        onChange={onTimeChange}
        value={startMoment && endMoment ? [startMoment, endMoment] : null}
      />
    </Col>
  );

  const allDayComponent = (
    <Col span={showCheckBoxOnNewLine ? 24 : 6}>
      {!hideCheckbox && !hideTime && (
        <OnTraccrCheckbox
          value={isAllDay}
          onChange={toggleAllDay}
          label="All day?"
          checkboxClasses={['form-checkbox-container']}
        />
      )}
    </Col>
  );

  return (
    <>
      <Row gutter={gutter} align="bottom" style={style}>
        <Col span={datePickerSpan}>
          {rangeMode ? (
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              className="form-field-datetime-container"
              format={getRangeFormat(!showTime)}
              showTime={rangePickerProps}
              onChange={onRangeChange}
              allowClear={allowClear}
              value={[startMoment, endMoment]}
            />
          ) : (
            <DatePicker
              style={{ width: '100%' }}
              className="form-field-datetime-container"
              format="MMM Do YY"
              allowClear={allowClear}
              onChange={onDateChanged}
              value={startMoment}
            />
          )}
        </Col>
        {
        showRangePicker && !isHalfWidth
          ? pickerComponent
          : null
        }
        {showCheckBoxOnNewLine ? null : allDayComponent}
      </Row>
      {showCheckBoxOnNewLine ? allDayComponent : null}
      {
      showRangePicker && isHalfWidth
        ? pickerComponent
        : null
      }
    </>
  );
}

OnTraccrDatePickerRedesign.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  gutter: PropTypes.number,
  hideCheckbox: PropTypes.bool,
  hideTime: PropTypes.bool,
  singleDay: PropTypes.bool,
  allowClear: PropTypes.bool,
  style: PropTypes.shape({}),
  timezone: PropTypes.string,
  isFieldDisplay: PropTypes.bool,
  isHalfWidth: PropTypes.bool,
};

OnTraccrDatePickerRedesign.defaultProps = {
  value: [],
  gutter: 20,
  hideCheckbox: false,
  hideTime: false,
  singleDay: false,
  allowClear: false,
  style: {},
  timezone: DateTime.local().zoneName,
  isFieldDisplay: false,
  isHalfWidth: false,
};
